'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const project = {
  name: 'Neo Central',
  tagline: 'Ideally Located Between Srisailam & New Tirupati Highways',
  location: 'Between Srisailam and New Tirupati Highways, Amangal–Peddapur, Hyderabad',
  area: '12 Acres + 12 Acres Extension',
  status: 'New Launch',
  dtcpNumber: 'TLP No. 182/2023/H',
  totalPlots: '180+',
  plotSizes: ['100 sq. yds', '150 sq. yds', '200 sq. yds'],
  pricePerSqYd: '₹9,999',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ebf59c61-1774865956338.png",
  galleryImages: [
  {
    src: 'https://img.rocket.new/generatedImages/rocket_gen_img_14e86477b-1787057103688.png',
    alt: 'Aerial view of Neo Central premium plotted development near Regional Ring Road Amangal Peddapur with wide roads'
  },
  {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1fcc54689-1786114685165.png",
    alt: 'Green landscape and open plots at Neo Central residential development near Hyderabad'
  },
  {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_15778b2ee-1784350465107.png",
    alt: 'Wide internal roads and street lighting at Neo Central plotted venture'
  },
  {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1765e9eab-1787627202129.png",
    alt: 'Entrance gate and compound wall at Neo Central gated community plots'
  },
  {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_18668f717-1787627201914.png",
    alt: 'Community park and open spaces inside Neo Central plotted development'
  },
  {
    src: "https://img.rocket.new/generatedImages/rocket_gen_img_1896c65d9-1787627202644.png",
    alt: 'Children play area and landscaped gardens at Neo Central residential plots'
  }],

  amenities: [
  { icon: '🛣️', label: 'Wide BT Roads' },
  { icon: '💡', label: 'Underground Electricity' },
  { icon: '💧', label: 'Overhead Water Tank' },
  { icon: '🌳', label: 'Avenue Plantation' },
  { icon: '🏛️', label: 'Compound Wall' },
  { icon: '🔒', label: 'Gated Community' },
  { icon: '🏊', label: 'Clubhouse' },
  { icon: '🌿', label: 'Landscaped Parks' },
  { icon: '🚸', label: "Children\'s Play Area" },
  { icon: '🔦', label: 'Street Lighting' },
  { icon: '🚰', label: 'Drainage System' },
  { icon: '📡', label: 'CCTV Surveillance' }],

  specifications: [
  { label: 'Project Type', value: 'Residential Plotted Development' },
  { label: 'Total Area', value: '12 Acres + 12 Acres Extension' },
  { label: 'Total Plots', value: '180+' },
  { label: 'Plot Sizes', value: '100 – 200 sq. yds' },
  { label: 'Road Width', value: '30 ft, 40 ft & 60 ft' },
  { label: 'Approval', value: 'DTCP Approved' },
  { label: 'RERA Status', value: 'RERA Registered' },
  { label: 'Possession', value: 'Immediate' },
  { label: 'Facing', value: 'East, West, North, South' },
  { label: 'Connectivity', value: 'Dual Highway Access' }],

  highlights: [
  'Strategically located between Srisailam Highway and New Tirupati Highway — dual connectivity advantage',
  'Close to Regional Ring Road (RRR) — a major infrastructure catalyst for land appreciation',
  'DTCP approved and RERA registered — fully legally compliant with clear title deeds',
  'Ideal for investors seeking high-growth corridor plots near Hyderabad',
  'Surrounded by upcoming industrial and residential developments in Amangal–Peddapur belt']

};

export default function NeoCentralPage() {
  const [activeImage, setActiveImage] = useState(0);

  return (
    <main className="font-avenir">
      <Navbar />
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[400px] overflow-hidden">
        <img
          src={project?.galleryImages?.[activeImage]?.src}
          alt={project?.galleryImages?.[activeImage]?.alt}
          className="w-full h-full object-cover" />

        <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 via-brand-dark/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="container mx-auto max-w-6xl">
            <Link
              href="/#projects"
              className="inline-flex items-center gap-2 text-white/70 hover:text-brand-turmeric font-avenir text-sm mb-4 transition-colors duration-200">

              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Projects
            </Link>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="bg-brand-turmeric text-white text-xs font-semibold font-avenir px-3 py-1 rounded-full">
                {project?.status}
              </span>
              <span className="bg-brand-green/90 text-white text-xs font-semibold font-avenir px-3 py-1 rounded-full">
                DTCP Approved
              </span>
              <span className="bg-white/20 text-white text-xs font-semibold font-avenir px-3 py-1 rounded-full backdrop-blur-sm">
                RERA Registered
              </span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white font-avenir mb-2">{project?.name}</h1>
            <p className="text-white/80 font-avenir text-sm md:text-base">{project?.tagline}</p>
          </div>
        </div>
      </section>
      {/* Gallery Thumbnails */}
      <section className="bg-brand-dark py-4">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {project?.galleryImages?.map((img, idx) =>
            <button
              key={idx}
              onClick={() => setActiveImage(idx)}
              className={`flex-shrink-0 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
              activeImage === idx ? 'border-brand-turmeric' : 'border-white/20 hover:border-white/50'}`
              }>

                <img src={img?.src} alt={img?.alt} className="w-full h-full object-cover" />
              </button>
            )}
          </div>
        </div>
      </section>
      {/* Main Content */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-3 gap-10">

            {/* Left — Details */}
            <div className="lg:col-span-2 space-y-10">

              {/* Key Highlights */}
              <div>
                <h2 className="text-2xl font-bold text-brand-dark font-avenir mb-3">Project Highlights</h2>
                <hr className="border-0 h-0.5 bg-brand-green w-16 mb-5" />
                <ul className="space-y-3">
                  {project?.highlights?.map((h, i) =>
                  <li key={i} className="flex items-start gap-3">
                      <span className="w-5 h-5 rounded-full bg-brand-green/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-brand-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                      <span className="text-brand-gray font-avenir text-sm leading-relaxed">{h}</span>
                    </li>
                  )}
                </ul>
              </div>

              {/* Specifications */}
              <div>
                <h2 className="text-2xl font-bold text-brand-dark font-avenir mb-3">Specifications</h2>
                <hr className="border-0 h-0.5 bg-brand-green w-16 mb-5" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {project?.specifications?.map((spec) =>
                  <div
                    key={spec?.label}
                    className="flex items-start gap-3 p-4 rounded-xl bg-brand-bg-light border border-brand-green/10">

                      <div className="flex-1">
                        <p className="text-brand-gray font-avenir text-xs mb-0.5">{spec?.label}</p>
                        <p className="text-brand-dark font-avenir text-sm font-semibold">{spec?.value}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Project Layout */}
              <div>
                <h2 className="text-2xl font-bold text-brand-dark font-avenir mb-3">Project Layout</h2>
                <hr className="border-0 h-0.5 bg-brand-green w-16 mb-5" />
                <div className="rounded-2xl overflow-hidden border border-brand-green/10 shadow-sm">
                  <img
                    src="/assets/images/neo_capital_group_layout_under_5mb-1788335363084.jpg"
                    alt="Neo Central project layout plan showing plot arrangement, roads, and amenities"
                    className="w-full h-auto object-contain"
                  />
                </div>
              </div>

              {/* Amenities */}
              <div>
                <h2 className="text-2xl font-bold text-brand-dark font-avenir mb-3">Amenities</h2>
                <hr className="border-0 h-0.5 bg-brand-green w-16 mb-5" />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {project?.amenities?.map((amenity) =>
                  <div
                    key={amenity?.label}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-brand-bg-light border border-brand-green/10 hover:border-brand-green/30 hover:shadow-sm transition-all duration-200 text-center">

                      <span className="text-2xl">{amenity?.icon}</span>
                      <span className="text-brand-dark font-avenir text-xs font-medium leading-tight">{amenity?.label}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* RERA Status */}
              <div>
                <h2 className="text-2xl font-bold text-brand-dark font-avenir mb-3">RERA & Legal Status</h2>
                <hr className="border-0 h-0.5 bg-brand-green w-16 mb-5" />
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-5 rounded-xl bg-brand-green/5 border border-brand-green/20">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-full bg-brand-green/10 flex items-center justify-center">
                        <svg className="w-4 h-4 text-brand-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className="text-brand-green font-avenir text-sm font-bold">RERA Registered</span>
                    </div>
                    <p className="text-brand-gray font-avenir text-xs mt-1">Registered with Telangana RERA Authority</p>
                  </div>
                  <div className="p-5 rounded-xl bg-brand-turmeric/5 border border-brand-turmeric/20">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-full bg-brand-turmeric/10 flex items-center justify-center">
                        <svg className="w-4 h-4 text-brand-turmeric" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className="text-brand-turmeric font-avenir text-sm font-bold">DTCP Approved</span>
                    </div>
                    <p className="text-brand-gray font-avenir text-xs">TLP No: <span className="text-brand-dark font-semibold">{project?.dtcpNumber}</span></p>
                    <p className="text-brand-gray font-avenir text-xs mt-1">Approved by Directorate of Town & Country Planning</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right — Pricing & Enquiry */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-5">

                {/* Pricing Card */}
                <div className="bg-brand-dark rounded-2xl p-6 text-white">
                  <h3 className="font-avenir font-bold text-base mb-4 text-brand-turmeric">Pricing</h3>
                  <div className="space-y-3 mb-5">
                    <div className="flex justify-between items-center py-2 border-b border-white/10">
                      <span className="text-white/70 font-avenir text-xs">Price per sq. yd</span>
                      <span className="text-white font-avenir text-sm font-bold">{project?.pricePerSqYd}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-white/10">
                      <span className="text-white/70 font-avenir text-xs">Total Plots</span>
                      <span className="text-white font-avenir text-sm font-bold">{project?.totalPlots}</span>
                    </div>
                  </div>
                  <a
                    href="/#contact"
                    className="block text-center py-3 bg-brand-turmeric text-white font-bold rounded-xl font-avenir text-sm hover:bg-brand-turmeric-light transition-colors duration-200">

                    Enquire Now
                  </a>
                </div>

                {/* Location Card */}
                <div className="bg-brand-bg-light rounded-2xl p-5 border border-brand-green/10">
                  <h3 className="font-avenir font-bold text-sm text-brand-dark mb-3">Location</h3>
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-brand-turmeric flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <p className="text-brand-gray font-avenir text-xs leading-relaxed">{project?.location}</p>
                  </div>
                </div>

                {/* Contact Quick */}
                <div className="bg-brand-green/5 rounded-2xl p-5 border border-brand-green/20">
                  <h3 className="font-avenir font-bold text-sm text-brand-dark mb-3">Need Help?</h3>
                  <p className="text-brand-gray font-avenir text-xs mb-4">Our team is available to answer your questions about Neo Central.</p>
                  <a
                    href="tel:+917569962831"
                    className="flex items-center gap-2 text-brand-green font-avenir text-sm font-semibold hover:text-brand-green/80 transition-colors duration-200">

                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    +91 7569962831
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </main>);

}